import { useEffect, useRef } from 'react';

import { useTheme } from 'styled-components';

import { useAppSelector } from 'hooks/reduxHooks';
import useFeatureSwitch from 'hooks/useFeatureSwitch/useFeatureSwitch';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import * as Styled from './BuilderAssistant.styled';

interface ResizeEvent {
  fullscreen: boolean;
  height: number;
  width: number;
}

const WebChatIframe = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { activeColorScheme } = useTheme();

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useEffect(() => {
    const webChatUrl = 'https://webchat.us-1.polyai.app/app/index.html';
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    iframe.src = webChatUrl;
    iframe.id = 'poly-ai-chat';

    const query = '(max-device-width: 300px)';
    const matchMedia = window.matchMedia(query);

    const getMatches = () => matchMedia.matches;

    const postIsMobile = (isMobile: boolean) => {
      iframe.contentWindow!.postMessage(
        {
          type: 'RESIZE',
          isMobile,
        },
        webChatUrl,
      );
    };

    const handleChange = () => {
      const matches = getMatches();
      postIsMobile(matches);
    };

    const initWebChat = () => {
      handleChange();
      iframe.contentWindow!.postMessage(
        {
          type: 'INIT',
          connectorToken:
            process.env.NEXT_PUBLIC_BUILDER_ASSISTANT_CONNECTOR_TOKEN,
          headerText: 'Builder Assistance',
          agentName: 'Xenia',
          style: {
            primary: activeColorScheme.colors.iconAccent,
          },
        },
        webChatUrl,
      );
    };

    const handleResize = (data: ResizeEvent) => {
      if (data.fullscreen) {
        iframe.style.height = '100dvh';
        iframe.style.width = '100dvw';
      } else {
        iframe.style.height = `${data.height}px`;
        iframe.style.width = `${data.width}px`;
      }
    };

    const handleMessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case 'READY':
          initWebChat();
          break;
        case 'IFRAME_RESIZE':
          handleResize(event.data);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    matchMedia.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('message', handleMessage);
      matchMedia.removeEventListener('change', handleChange);
    };
  }, []);

  return <Styled.Iframe ref={iframeRef} title="WebChat" />;
};

const BuilderAssistant = () => {
  const { isFeatureOn } = useFeatureSwitch();
  const { inSampleProject } = useSampleProject();
  const isBuilderAssistantEnabled =
    isFeatureOn('builder_assistant') && !inSampleProject;
  const audioWrapperHeight = useAppSelector(
    state => state.layout.audioWrapperHeight,
  );

  if (!isBuilderAssistantEnabled) {
    return null;
  }

  return (
    <Styled.Wrapper $audioWrapperHeight={audioWrapperHeight}>
      <WebChatIframe />
    </Styled.Wrapper>
  );
};

export default BuilderAssistant;
